import { TextField, Typography, Divider, Button, Chip, IconButton, Tooltip, Dialog, DialogTitle, Stack, useTheme } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link, useLocation, useNavigate } from 'react-router';
import SearchIcon from '@mui/icons-material/Search';
import ChatIcon from '@mui/icons-material/Chat';
import { getTicketDetails } from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import ChatBox from '../ChatBox/ChatBox';
import React, { useState } from 'react'
import { Card } from '@mui/material'
import { marked } from "marked";

function convertToPlainText(content) {
    const htmlTagRegex = /<\/?[a-z][\s\S]*>/i;
    const markdownRegex = /[*_#\-`\[\]]|!\[.*\]\(.*\)/;

    const isHTML = htmlTagRegex.test(content);
    const isMarkdown = markdownRegex.test(content);

    if (!isHTML && !isMarkdown) {
        return content.trim();
    }

    // Convert Markdown to HTML if needed
    let html = isMarkdown && !isHTML ? marked.parse(content) : content;

    // Normalize spacing: add line breaks for relevant tags
    html = html
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(p|h[1-6]|div)>/gi, "\n")
        .replace(/<\/li>/gi, "\n")
        .replace(/<li[^>]*>/gi, "- ")
        .replace(/<\/ul>|<\/ol>/gi, "\n")
        .replace(/<[^>]+>/g, ""); // Strip all other HTML tags

    // Decode HTML entities (e.g., &amp; → &)
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent.replace(/\n{3,}/g, "\n\n").trim(); // remove extra line breaks
}


const TicketComponent = (props) => {
    const [currentTicketId, setCurrentTicketId] = useState(props?.ticketId);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const ticketDetails = useSelector((state) => state.ticket.ticketDetails);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const newSearchParams = new URLSearchParams(location.search);
    const theme = useTheme();

    const [open, setOpen] = useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    function handleSubmit(event) {
        event.preventDefault();
        if (!event.target.checkValidity()) {
            event.stopPropagation();
            return;
        }
        setIsSubmitted(true);
        newSearchParams.set("ticketId", currentTicketId);
        navigate({ pathname: location.pathname, search: newSearchParams.toString() });
        dispatch(getTicketDetails(currentTicketId, props?.subDomain, props?.ticketingSystem, props?.orgId, props?.prismToken));
    }

    const navigateTicket = (currentTicketId, navigateDirection) => {
        let ticketToFetch = navigateDirection === "next" ? Number(currentTicketId) + 1 : Number(currentTicketId) - 1;
        ticketToFetch = String(ticketToFetch)
        setCurrentTicketId(ticketToFetch);
        newSearchParams.set("ticketId", ticketToFetch);
        navigate({ pathname: location.pathname, search: newSearchParams.toString() });
        dispatch(getTicketDetails(ticketToFetch, props?.subDomain, props?.ticketingSystem, props?.orgId, props?.prismToken));
    };


    return (
        <div>
            <Card sx={{ display: "flex", flexDirection: "column", padding: "20px", width: "100%", height: "100%", gap: "15px", borderRadius: "15px" }}>
                <form onSubmit={handleSubmit}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", columnGap: "10px" }}>
                        <Tooltip title="Previous Ticket" arrow>
                            <IconButton disabled={!currentTicketId.trim()} onClick={() => navigateTicket(currentTicketId, "previous")} >
                                <NavigateBeforeIcon />
                            </IconButton>
                        </Tooltip>
                        <TextField required size="small" fullWidth label="Ticket ID" variant="outlined" value={currentTicketId} onChange={(e) => setCurrentTicketId(e.target.value.trim())} error={!currentTicketId.trim()} />
                        <Tooltip title={`Fetch Ticket ${currentTicketId}`} arrow>
                            <Button disabled={!currentTicketId.trim()} type="submit" size="small" variant="contained" sx={{ width: "150px", textTransform: "none" }} endIcon={<SearchIcon />} >Search</Button>
                        </Tooltip>
                        <Tooltip title="Next Ticket" arrow>
                            <IconButton disabled={!currentTicketId.trim()} onClick={() => navigateTicket(currentTicketId, "next")} >
                                <NavigateNextIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </form>
                <Divider variant="fullWidth" sx={{ border: "1px solid" }} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography gutterBottom variant="body2" fontWeight="bold">
                        Ticket #
                        <Link target="_blank" rel="noopener noreferrer" to={ticketDetails.ticket_link || ""} style={{ fontSize: '0.8rem', textDecoration: "none", textTransform: "none" }}>
                            {ticketDetails.id || ""}
                        </Link>
                    </Typography>
                    <Button disabled={!ticketDetails?.id} size="small" sx={{ textTransform: "none" }} endIcon={<ChatIcon />} onClick={handleClickOpen} >View Conversation</Button>
                </div>
                <Typography gutterBottom variant="body1" fontWeight="bold"> {ticketDetails.subject || ""}</Typography>
                <Divider variant="fullWidth" sx={{ border: "1px solid" }} />
                <Typography gutterBottom variant="body1" fontWeight="bold"> Description</Typography>
                <Typography gutterBottom component="p"> {convertToPlainText(ticketDetails.description || "")}</Typography>
                <Divider variant="fullWidth" sx={{ border: "1px solid" }} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography gutterBottom variant="body1" fontWeight="bold" >Status</Typography>
                    {ticketDetails.status ? <Chip label={ticketDetails.status} color="primary" variant="outlined" /> : <Typography gutterBottom >NA</Typography>}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography gutterBottom variant="body1" fontWeight="bold">Priority</Typography>
                    {ticketDetails.priority ? <Chip label={ticketDetails.priority} color="primary" variant="outlined" /> : <Typography gutterBottom >NA</Typography>}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography gutterBottom variant="body1" fontWeight="bold">Requester Name</Typography>
                    <Typography gutterBottom > {ticketDetails.requester_name || "NA"}</Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography gutterBottom variant="body1" fontWeight="bold">Requester Email</Typography>
                    <Typography gutterBottom > {ticketDetails.requester_email || "NA"}</Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography gutterBottom variant="body1" fontWeight="bold">Assignee Name</Typography>
                    <Typography gutterBottom > {ticketDetails.assignee_name || "NA"}</Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography gutterBottom variant="body1" fontWeight="bold">Assignee Email</Typography>
                    <Typography gutterBottom > {ticketDetails.assignee_email || "NA"}</Typography>
                </div>
                {Array.isArray(ticketDetails.tags) && ticketDetails.tags.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography gutterBottom variant="body1" fontWeight="bold">Tags</Typography>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            {ticketDetails.tags.map((item, index) => (
                                <Chip key={index} label={item} color="primary" variant="outlined" />
                            ))}
                        </div>
                    </div>
                )}

            </Card>
            <Dialog open={open} onClose={handleClose} maxWidth="lg" slotProps={{ paper: { sx: { borderRadius: 4, overflow: 'hidden' } } }} >
                <DialogTitle align="center">Ticket #{ticketDetails.id} Conversation</DialogTitle>
                <Stack direction="row" spacing={1} padding={1} justifyContent="center" >
                    <Chip label="Agent Comment" sx={{ borderRadius: 2, backgroundColor: theme.palette.grey[300], color: "black"}} />
                    <Chip label="Internal Comment" sx={{ borderRadius: 2, backgroundColor: theme.palette.warning.light, color: "black"}} />
                    <Chip label="Customer Comment" sx={{ borderRadius: 2, backgroundColor: theme.palette.primary.main, color: "white" }} />
                </Stack>
                {
                    ticketDetails?.comments && ticketDetails?.comments.length > 0
                        ?
                        <ChatBox comments={ticketDetails?.comments || []} ticketDetails={ticketDetails} />
                        :
                        <Typography gutterBottom variant="body1" sx={{ padding: 5 }}>{`No Conversation Found For Ticket #${ticketDetails.id}`}</Typography>
                }
            </Dialog>
        </div>
    )
}

export default TicketComponent